import { memo, useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { addUserMessage, messagesSelectors, setChatStatus } from 'store/messages'
import { settingsSelectors } from 'store/settings'
import { createUserMessage } from 'utils/helpers'
import { useTranslation } from 'react-i18next'
import { OdinIcon } from '@/components/ui/odin/odin-icon'
import { InputGroup, InputGroupAddon, InputGroupButton } from '@/components/ui/input-group'
import { ComposerTextarea } from 'components/ui/ComposerTextarea'
import { ComposerAttachments } from 'components/ComposerAttachments'
import { VoiceRecorderBar } from 'components/VoiceRecorderBar'
import { askBotAnswer } from '@/api'
import { sentMessageToOperator } from 'api/chats'
import { CHAT_ATTACHMENT_ACCEPT } from 'utils/chat-attachments'
import { WidgetFileUpload } from 'components/WidgetFileUpload'
import { Spinner } from '@/components/ui/spinner'
import { useComposerAttachments } from 'hooks/useComposerAttachments'
import { useVoiceComposer } from 'hooks/useVoiceComposer'
import { audioPlayer } from 'services/audio-player'
import { trackBotMessageSent } from 'services/analytics'

const ChatTextInputComponent = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [userInput, setUserInput] = useState('')
  const chatId = useSelector(messagesSelectors.chatId)
  const userTypingPlaceholder = useSelector(messagesSelectors.userTypingPlaceholder)
  const isInOperatorMode = useSelector(messagesSelectors.currentChatIsInOperatorMode)
  const disableFields = useSelector(messagesSelectors.disableFieldsTicketClosed)
  const isSending = useSelector(messagesSelectors.currentChatSending)
  const chatAttachmentsEnabled = useSelector(settingsSelectors.chatAttachmentsEnabled)
  const voiceEnabled = useSelector(settingsSelectors.voiceEnabled)

  const attachmentsEnabled = isInOperatorMode || chatAttachmentsEnabled
  const attachments = useComposerAttachments({ chatId, isInOperatorMode })

  const sendBotQuestion = useCallback(
    async (question: string, options?: { voice?: boolean }) => {
      const sendingChatId = chatId
      if (!sendingChatId) return
      const messageAttachments = attachments.takeAttachments()
      setUserInput('')
      const { message, _id: questionId, senderId, chatSenderRole } = createUserMessage(question)
      dispatch(
        addUserMessage({
          chatId: sendingChatId,
          _id: questionId,
          message,
          senderId,
          chatSenderRole,
          attachments: messageAttachments.length ? messageAttachments : undefined,
        }),
      )
      trackBotMessageSent({
        source: options?.voice ? 'voice' : 'typed',
        attachmentCount: messageAttachments.length,
      })
      // askBotAnswer owns the bot status (awaitingBot -> idle); we only add the message.
      await askBotAnswer({
        question,
        questionId,
        attachmentIds: messageAttachments.map((attachment) => attachment._id),
        voice: options?.voice,
      })
    },
    [chatId, attachments, dispatch],
  )

  const voice = useVoiceComposer({
    chatId,
    onTranscribed: (text) => sendBotQuestion(text, { voice: true }),
  })

  const handleSubmit = useCallback(async () => {
    const sendingChatId = chatId
    if (!sendingChatId) return
    const question = userInput.trim()

    if (!question || attachments.isLoadingAttachments || isSending || voice.transcribing) return
    audioPlayer.stopAll() // a new message interrupts whatever the bot is saying

    try {
      if (isInOperatorMode) {
        const filesToSend = attachments.takeFiles()
        setUserInput('')
        dispatch(setChatStatus({ chatId: sendingChatId, status: 'sending' }))
        try {
          await sentMessageToOperator({ message: question, files: filesToSend })
        } finally {
          dispatch(setChatStatus({ chatId: sendingChatId, status: 'idle' }))
        }
        return
      }

      await sendBotQuestion(question)
    } catch (e) {
      console.error({ e })
    }
  }, [dispatch, userInput, isInOperatorMode, isSending, chatId, attachments, voice.transcribing, sendBotQuestion])

  const busy = isSending || voice.transcribing
  const sendDisabled = disableFields || busy || attachments.isLoadingAttachments || !userInput.trim()
  const showSend = !!userInput.trim() || busy
  const showMic = !showSend && voiceEnabled && !isInOperatorMode && !disableFields

  return (
    <div className="flex w-full flex-col gap-1.5">
      <ComposerAttachments uploads={attachments.uploads} onRemove={attachments.onRemove} removeDisabled={isSending} />
      <InputGroup className="h-auto items-end bg-background has-disabled:bg-background has-disabled:opacity-100 dark:has-disabled:bg-background">
        {attachmentsEnabled && (
          <InputGroupAddon align="inline-start" className="py-1">
            <WidgetFileUpload
              disable={disableFields || busy || voice.isRecording}
              accept={isInOperatorMode ? undefined : CHAT_ATTACHMENT_ACCEPT}
              onFilesAdded={attachments.onFilesAdded}
            />
          </InputGroupAddon>
        )}

        {voice.isRecording ? (
          <VoiceRecorderBar elapsed={voice.elapsed} stream={voice.stream} starting={voice.isStarting} />
        ) : (
          <ComposerTextarea
            value={userInput}
            onChange={setUserInput}
            onSubmit={() => handleSubmit().catch(console.error)}
            placeholder={voice.transcribing ? t('voiceTranscribing') : t(userTypingPlaceholder)}
            disabled={disableFields}
          />
        )}

        {voice.isRecording ? (
          <InputGroupAddon key="voice-actions" align="inline-end" className="gap-1 py-1">
            <InputGroupButton
              key="cancel"
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={t('voiceCancel')}
              onClick={voice.cancel}
            >
              <OdinIcon aria-hidden icon={['far', 'xmark']} />
            </InputGroupButton>
            <InputGroupButton
              key="confirm"
              autoFocus
              type="button"
              variant="default"
              size="icon-sm"
              disabled={voice.isStarting}
              aria-label={t('voiceStopAndSend')}
              onClick={voice.confirm}
            >
              <OdinIcon aria-hidden icon={['far', 'check']} />
            </InputGroupButton>
          </InputGroupAddon>
        ) : (
          (showSend || showMic) && (
            <InputGroupAddon key="composer-action" align="inline-end" className="animate-scale-in py-1">
              {/* One slot, two roles: mic when empty (voice tenants), send when there is text. */}
              <InputGroupButton
                type="button"
                variant="default"
                size="icon-sm"
                disabled={showMic ? busy : sendDisabled}
                aria-label={showMic ? t('voiceRecord') : t('sendMessage')}
                onClick={() => (showMic ? voice.startRecording() : handleSubmit().catch(console.error))}
              >
                {busy ? (
                  <Spinner aria-hidden />
                ) : (
                  <span key={showMic ? 'mic' : 'send'} className="animate-scale-in inline-flex">
                    <OdinIcon aria-hidden icon={['far', showMic ? 'microphone' : 'arrow-up']} />
                  </span>
                )}
              </InputGroupButton>
            </InputGroupAddon>
          )
        )}
      </InputGroup>
    </div>
  )
}

export const ChatTextInput = memo(ChatTextInputComponent)
ChatTextInput.displayName = 'ChatTextInput'
